import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Settings,
  Globe,
  Shield,
  Star,
  Clock,
  DollarSign,
  Key,
  Link as LinkIcon,
  Trash2,
  Loader2,
  ChevronLeft,
  TrendingUp,
  Hash,
  Bot,
  Zap,
  Power,
  Grid,
  ListChecks
} from 'lucide-react'
import { useResource, useToggleResource, useDeleteResource, useUpdateResource } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useUser } from '@/shared/hooks/useUser'
import { useBackButton } from '@/shared/hooks/useBackButton'
import Button from '@/components/ui/Button'
import Toast from '@/components/ui/Toast'
import PullToRefresh from '@/components/ui/PullToRefresh'
import { getHoldingFeeAmount } from '@/shared/utils/format'

export default function ResourceDetails() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { hapticFeedback, hapticNotification, showPopup, telegramId } = useTelegram()
  const { data: resource, isLoading, error, refetch } = useResource(resourceId)
  const { data: user } = useUser(telegramId)
  const toggleResource = useToggleResource()
  const deleteResource = useDeleteResource()
  const updateResource = useUpdateResource()
  const [isDeleting, setIsDeleting] = useState(false)
  const [imageLoading, setImageLoading] = useState(true)
  const [imageError, setImageError] = useState(false)
  const [toastMessage, setToastMessage] = useState<string>('')
  const [showToast, setShowToast] = useState(false)

  // Auto-manage back button with custom handler if returnTo is provided
  const returnTo = (location.state as { returnTo?: string })?.returnTo
  useBackButton({
    onBack: returnTo ? () => {
      window.scrollTo(0, 0)
      navigate(returnTo)
    } : undefined
  })

  const handleRefresh = async () => {
    try {
      await refetch()
      hapticNotification?.('success')
    } catch (error) {
      console.error('Failed to refresh resource data:', error)
      hapticNotification?.('error')
    }
  }

  // Show toast if navigation state contains success message
  useEffect(() => {
    const state = location.state as { successMessage?: string } | null
    if (state?.successMessage) {
      setToastMessage(state.successMessage)
      setShowToast(true)
      // Clear navigation state after a small delay to allow toast to render
      setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} })
      }, 100)
    }
  }, [location.state, navigate, location.pathname])

  const getLogoUrl = () => {
    if (!resource?.id) return null

    const baseUrl = import.meta.env.VITE_OPSERVICE_BASE_URL || 'http://localhost:8080'
    return `${baseUrl}/api/v1/resources/${resource.id}/logo`
  }

  const getResourceIcon = () => {
    if (!resource) return <Hash className="w-8 h-8" />

    switch (resource.resource_type) {
      case 'bot':
        return <Bot className="w-8 h-8" />
      case 'boost':
        return <Zap className="w-8 h-8" />
      case 'channel':
      case 'group':
      default:
        return <Hash className="w-8 h-8" />
    }
  }

  const handleToggle = async () => {
    if (!resource || !user) return

    const currentStatus = resource.status || 'inactive'
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active'

    // If activating, check if it's possible
    if (newStatus === 'active') {
      // Check available balance for holding fee
      const requiredFee = getHoldingFeeAmount(resource.resource_type)
      const available = user.balance - user.resource_retention

      if (available < requiredFee) {
        hapticNotification('error')
        await showPopup({
          title: '⚠️ Недостаточно средств',
          message: `Для активации ресурса необходимо ${requiredFee} USDT доступного баланса.\n\nВаш баланс:\n• Всего: ${user.balance.toFixed(2)} USDT\n• Удержано на ресурсах: ${user.resource_retention.toFixed(2)} USDT\n• Доступно: ${available.toFixed(2)} USDT\n\nНеобходимо: ${requiredFee} USDT`,
          buttons: [{ id: 'ok', type: 'default', text: 'OK' }]
        })
        return
      }

      // Check if resource requires validation
      const requiresValidation =
        ['verified', 'application'].includes(resource.verification_mode || 'unverified') ||
        resource.resource_type === 'boost'

      if (requiresValidation) {
        // Check if verification bot token is set
        if (!resource.verification_bot_token) {
          hapticNotification('error')
          await showPopup({
            title: '⚠️ Токен не установлен',
            message: 'Необходимо установить токен верификационного бота перед активацией ресурса.',
            buttons: [{ id: 'ok', type: 'default', text: 'OK' }]
          })
          return
        }

        // Bot resources can be identified from their token on the backend.
        if (resource.resource_type !== 'bot' && !resource.telegram_id) {
          hapticNotification('error')
          await showPopup({
            title: '⚠️ Telegram ID не установлен',
            message: 'Необходимо указать Telegram ID ресурса.',
            buttons: [{ id: 'ok', type: 'default', text: 'OK' }]
          })
          return
        }

        // Backend verifies bot-resource tokens directly and checks channel admin only for channel-like resources.
        // No confirmation needed - server-side validation handles it.
      }
    }

    try {
      hapticFeedback('medium')
      await toggleResource.mutateAsync({ resourceId: resource.id, newStatus })
      hapticNotification('success')
    } catch (err: any) {
      console.error('Error toggling resource:', err)
      hapticNotification('error')

      // Get error details from API response
      const apiError = err?.response?.data?.error || ''
      const apiDetails = err?.response?.data?.details || ''

      // Map bot verification errors to user-friendly Russian messages
      let title = '❌ Ошибка'
      let message = 'Не удалось изменить статус ресурса'

      if (apiError.includes('Bot verification failed')) {
        title = '⚠️ Ошибка верификации'

        if (apiDetails.includes('channel not found') || apiDetails.includes('chat not found')) {
          message = 'Канал не найден.\n\nУбедитесь, что Telegram ID указан правильно.\n\nТelegram ID должен быть числовым идентификатором канала (например: -1001234567890)'
        } else if (apiDetails.includes('invalid verification bot token') || apiDetails.includes('invalid bot token')) {
          message = 'Неверный токен бота.\n\nПроверьте правильность токена верификационного бота.\n\nПолучите новый токен у @BotFather, если необходимо'
        } else if (apiDetails.includes('bot not added to channel')) {
          message = 'Бот не добавлен в канал.\n\n1. Добавьте верификационный бот в канал как участника\n2. Назначьте его администратором'
        } else if (apiDetails.includes('bot is not channel administrator')) {
          message = 'Бот не является администратором канала.\n\nОткройте настройки канала и назначьте верификационный бот администратором с правами управления участниками'
        } else {
          message = 'Не удалось проверить статус бота.\n\nПроверьте что:\n\n• Бот добавлен в канал\n• Бот является администратором'
        }
      } else {
        // Other errors - use API message or default
        message = apiError || err?.message || message
      }

      await showPopup({
        title,
        message,
        buttons: [{ id: 'ok', type: 'default', text: 'OK' }]
      })
    }
  }

  const handleDelete = async () => {
    if (!resource) return

    hapticFeedback('medium')

    const buttonId = await showPopup({
      title: 'Удалить ресурс?',
      message: `Вы уверены, что хотите удалить ${resource.name}? Это действие необратимо.`,
      buttons: [
        { id: 'cancel', type: 'cancel', text: 'Отмена' },
        { id: 'delete', type: 'destructive', text: 'Удалить' }
      ]
    })

    if (buttonId !== 'delete') {
      return
    }

    try {
      setIsDeleting(true)
      hapticFeedback('medium')
      await deleteResource.mutateAsync(resource.id)
      hapticNotification('success')
      navigate('/traffic')
    } catch (err) {
      console.error('Error deleting resource:', err)
      hapticNotification('error')
      setIsDeleting(false)
    }
  }

  const handleNavigate = (path: string) => {
    hapticFeedback('light')
    window.scrollTo(0, 0)
    navigate(`/traffic/${resourceId}/${path}`)
  }

  const handleWhitelist = () => {
    hapticFeedback('light')
    window.scrollTo(0, 0)
    navigate(`/traffic/${resourceId}/whitelist`)
  }

  const handleToggleAutoResource = async () => {
    if (!resource) return

    hapticFeedback('medium')
    try {
      await updateResource.mutateAsync({
        resourceId: resource.id,
        data: { auto_resource: !resource.auto_resource }
      })
      setToastMessage(resource.auto_resource ? 'Авто-ресурс отключен' : 'Авто-ресурс включен')
      setShowToast(true)
    } catch (error) {
      console.error('Failed to toggle auto_resource:', error)
      hapticNotification('error')
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  if (error || !resource) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">Ресурс не найден</h2>
          <Button onClick={() => navigate('/traffic')}>Вернуться</Button>
        </div>
      </div>
    )
  }

  const getStatusBadge = () => {
    const statusStyles = {
      active: {
        label: 'Активен',
        bgColor: 'bg-green-500/10',
        textColor: 'text-green-600',
        borderColor: 'border-green-500/30',
        dotColor: 'bg-green-500',
      },
      inactive: {
        label: 'Неактивен',
        bgColor: 'bg-gray-500/10',
        textColor: 'text-gray-600',
        borderColor: 'border-gray-500/30',
        dotColor: 'bg-gray-500',
      },
      pending: {
        label: 'Ожидает проверки',
        bgColor: 'bg-yellow-500/10',
        textColor: 'text-yellow-600',
        borderColor: 'border-yellow-500/30',
        dotColor: 'bg-yellow-500',
      },
      rejected: {
        label: 'Отклонен',
        bgColor: 'bg-red-500/10',
        textColor: 'text-red-500',
        borderColor: 'border-red-500/30',
        dotColor: 'bg-red-500',
      },
      maintenance: {
        label: 'На обслуживании',
        bgColor: 'bg-orange-500/10',
        textColor: 'text-orange-600',
        borderColor: 'border-orange-500/30',
        dotColor: 'bg-orange-500',
      },
    }

    // For pending status: only show "Ожидает проверки" if auto_resource is true
    // For non-auto-resources, treat pending as inactive (no moderation needed)
    let effectiveStatus = resource.status
    if (resource.status === 'pending' && !resource.auto_resource) {
      effectiveStatus = 'inactive'
    }

    const style = statusStyles[effectiveStatus as keyof typeof statusStyles] || statusStyles.inactive

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${style.bgColor} ${style.textColor} border ${style.borderColor}`}>
        <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${style.dotColor}`} />
        {style.label}
      </span>
    )
  }

  // Quick Settings items
  const quickSettings = [
    {
      id: 'statistics',
      icon: TrendingUp,
      title: 'Статистика',
      description: 'Подробная аналитика',
      color: '#059669',
      bgColor: 'bg-emerald-500/20',
      show: true,
    },
    {
      id: 'filters',
      icon: Settings,
      title: 'Фильтры',
      description: 'Настройка аудитории',
      color: '#3b82f6',
      bgColor: 'bg-blue-500/20',
      show: true,
    },
    {
      id: 'geo',
      icon: Globe,
      title: 'Гео-таргетинг',
      description: 'Выбор стран',
      color: '#10b981',
      bgColor: 'bg-green-500/20',
      show: true,
    },
    {
      id: 'categories',
      icon: Grid,
      title: 'Категории',
      description: 'Категории ботов',
      color: '#8b5cf6',
      bgColor: 'bg-purple-500/20',
      show: resource.resource_type !== 'boost' && resource.auto_resource,
    },
    {
      id: 'mode',
      icon: Shield,
      title: 'Режим проверки',
      description: resource.verification_mode === 'verified' ? 'С проверкой' : resource.verification_mode === 'application' ? 'Заявки' : 'Без проверки',
      color: '#06b6d4',
      bgColor: 'bg-cyan-500/20',
      show: resource.resource_type !== 'boost',
    },
    {
      id: 'priority',
      icon: Star,
      title: 'Приоритет',
      description: `${resource.priority_price > 0 ? `${resource.priority_price} руб.` : 'Выключен'}`,
      color: '#f59e0b',
      bgColor: 'bg-amber-500/20',
      show: resource.auto_resource,
    },
    {
      id: 'limit',
      icon: Clock,
      title: 'Лимит',
      description: resource.retention ? `Удержание: ${resource.retention}` : resource.resource_limit ? `За день: ${resource.resource_limit}` : 'Без лимита',
      color: '#ec4899',
      bgColor: 'bg-pink-500/20',
      show: true,
    },
  ]

  // Advanced Settings items
  const advancedSettings = [
    {
      id: 'price',
      icon: DollarSign,
      title: 'Изменить цену',
      description: `${resource.final_reward || resource.base_reward} руб.`,
      color: '#10b981',
      bgColor: 'bg-emerald-500/20',
      show: resource.auto_resource,
    },
    {
      id: 'token',
      icon: Key,
      title: 'Токен бота',
      description: resource.verification_bot_token ? 'Настроен' : 'Не настроен',
      color: '#a855f7',
      bgColor: 'bg-purple-500/20',
      show: resource.resource_type !== 'bot',
    },
    {
      id: 'link',
      icon: LinkIcon,
      title: 'Ссылка',
      description: 'Обновить ссылку',
      color: '#6366f1',
      bgColor: 'bg-indigo-500/20',
      show: true,
    },
  ]

  const logoUrl = getLogoUrl()
  const isActive = resource.status === 'active'

  // Check if token is required but not configured
  const requiresToken =
    ['verified', 'application'].includes(resource.verification_mode || 'unverified') ||
    resource.resource_type === 'boost'
  const isTokenMissing = requiresToken && !resource.verification_bot_token

  return (
    <div className="min-h-screen bg-tg-bg pb-32">
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="pt-[96px]">
          {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
          <div className="flex items-center gap-4">
            {/* Resource Logo or Icon */}
            <div className={`relative flex-shrink-0 w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center ${
              isActive
                ? 'bg-tg-button shadow-lg shadow-tg-button/25'
                : 'bg-tg-bg'
            }`}>
              {logoUrl && !imageError ? (
                <>
                  <img
                    src={logoUrl}
                    alt={`${resource.name} logo`}
                    className={`w-full h-full object-cover transition-opacity duration-200 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                    onError={() => {
                      setImageError(true)
                      setImageLoading(false)
                    }}
                    onLoad={() => {
                      setImageLoading(false)
                    }}
                  />
                  {imageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className={`w-5 h-5 animate-spin ${isActive ? 'text-tg-button-text' : 'text-[#6d7885]'}`} />
                    </div>
                  )}
                </>
              ) : (
                <div className={isActive ? 'text-tg-button-text' : 'text-[#6d7885]'}>
                  {getResourceIcon()}
                </div>
              )}
            </div>

            {/* Resource Info */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-tg-text">{resource.name}</h1>
              <p className="text-sm text-tg-hint">{resource.telegram_id || 'ID не указан'}</p>
            </div>
          </div>

          {/* Status Badge on new line */}
          <div>
            {getStatusBadge()}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Stats Cards - only show price card if auto_resource is true */}
        {resource.auto_resource && (
          <div className="space-y-4">
            {/* Price Card */}
            <div className="bg-tg-secondary-bg rounded-2xl p-4 shadow-sm border border-[#059669]">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-[#059669]" />
                <span className="text-xs text-tg-hint">Цена за подписчика</span>
              </div>
              <div className="text-2xl font-bold text-tg-text">
                {resource.final_reward || resource.base_reward} ₽
              </div>
            </div>
          </div>
        )}

        {/* Self-Promotion Settings */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm border border-tg-secondary-bg overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Свои боты</h2>
          </div>

          <div className="flex items-center justify-between p-4 border-b border-tg-secondary-bg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                <Zap className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-tg-text">Авто-ресурс</div>
                <div className="text-xs text-tg-hint">
                  {resource.auto_resource ? 'Автоматический подбор ботов' : 'Только выбранные боты'}
                </div>
              </div>
            </div>
            <button
              onClick={handleToggleAutoResource}
              disabled={updateResource.isPending}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                resource.auto_resource ? 'bg-[#059669]' : 'bg-tg-bg'
              } ${updateResource.isPending ? 'opacity-50' : ''}`}
            >
              <div
                className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  resource.auto_resource ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <button
            onClick={handleWhitelist}
            className="w-full flex items-center justify-between p-4 hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <ListChecks className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-tg-text">Мои боты</div>
                <div className="text-xs text-tg-hint">Выбор своих ботов для продвижения</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-tg-hint/50 rotate-180" />
          </button>
        </div>

        {/* Quick Settings */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm border border-tg-secondary-bg overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Быстрые настройки</h2>
          </div>

          {quickSettings.filter(item => item.show).map((item, index, array) => {
            const Icon = item.icon
            const isLast = index === array.length - 1

            return (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                className={`w-full flex items-center justify-between p-4 hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors ${!isLast ? 'border-b border-tg-secondary-bg' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.bgColor}`}>
                    <Icon className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-tg-text">{item.title}</div>
                    <div className="text-xs text-tg-hint">{item.description}</div>
                  </div>
                </div>
                <ChevronLeft className="w-5 h-5 text-tg-hint/50 rotate-180" />
              </button>
            )
          })}
        </div>

        {/* Advanced Settings */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm border border-tg-secondary-bg overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Дополнительно</h2>
          </div>

          {advancedSettings.filter(item => item.show).map((item, index, array) => {
            const Icon = item.icon
            const isLast = index === array.length - 1
            const isTokenItem = item.id === 'token'
            const needsRedBorder = isTokenItem && isTokenMissing

            return (
              <div key={item.id} className={needsRedBorder ? 'p-2' : ''}>
                <button
                  onClick={() => handleNavigate(item.id)}
                  className={`w-full flex items-center justify-between p-4 transition-colors ${
                    needsRedBorder
                      ? 'border-2 border-red-500 rounded-xl active:scale-[0.98]'
                      : `hover:bg-tg-hint/10 active:bg-tg-hint/20 ${!isLast ? 'border-b border-tg-secondary-bg' : ''}`
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${needsRedBorder ? 'bg-red-500/20' : item.bgColor}`}>
                      <Icon className="w-5 h-5" style={{ color: needsRedBorder ? '#ef4444' : item.color }} />
                    </div>
                    <div className="text-left">
                      <div className={`font-medium ${needsRedBorder ? 'text-red-600' : 'text-tg-text'}`}>
                        {needsRedBorder && '⚠️ '}{item.title}
                      </div>
                      <div className={`text-xs ${needsRedBorder ? 'text-red-600' : 'text-tg-hint'}`}>
                        {item.description}
                      </div>
                    </div>
                  </div>
                  <ChevronLeft className={`w-5 h-5 rotate-180 ${needsRedBorder ? 'text-red-500' : 'text-tg-hint/50'}`} />
                </button>
              </div>
            )
          })}
        </div>

        {/* Toggle Status Button */}
        <button
          onClick={handleToggle}
          disabled={toggleResource.isPending || (resource.status === 'pending' && resource.auto_resource)}
          className={`w-full p-5 rounded-2xl font-semibold text-lg shadow-lg active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            resource.status === 'pending' && resource.auto_resource
              ? 'bg-tg-hint text-white'
              : isActive
              ? 'bg-red-600 text-white shadow-red-600/25'
              : 'bg-[#059669] text-white shadow-[#059669]/25'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Power className="w-6 h-6" />
            {resource.status === 'pending' && resource.auto_resource
              ? '⏳ На модерации'
              : toggleResource.isPending
              ? 'Обработка...'
              : isActive
              ? 'Остановить ресурс'
              : 'Запустить ресурс'}
          </div>
        </button>

        {/* Delete Resource (Owner Only) */}
        {user && resource.owner_id === user.id && (
          <button
            onClick={handleDelete}
            disabled={isDeleting || deleteResource.isPending}
            className="w-full p-4 rounded-2xl font-medium bg-red-500/10 text-red-600 border-2 border-red-500/30 hover:bg-red-500/20 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-4"
          >
            <div className="flex items-center justify-center gap-2">
              <Trash2 className="w-5 h-5" />
              {isDeleting || deleteResource.isPending ? 'Удаление...' : 'Удалить ресурс'}
            </div>
          </button>
        )}
      </div>
        </div>
      </PullToRefresh>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type="success"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  )
}
